import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react'

import { api } from '../api/repository'
import { readToken, writeToken } from '../api/client'
import type { UserProfile } from '../model/types'

/**
 * Глобальное состояние: сессия. Порт `App/AppState.swift`.
 * Всё остальное живёт в экранах — общий стор на весь домен быстро
 * превращается в свалку.
 */
interface AppStateValue {
  user: UserProfile | null
  isAuthenticated: boolean
  isBootstrapping: boolean
  signIn: (user: UserProfile, token: string) => void
  signOut: () => void
  /// Обновление профиля без смены сессии: кабинет сохранил правки, а
  /// токен остался прежним. Через `signIn` это делать нельзя — он
  /// перезаписал бы токен.
  setUser: (user: UserProfile) => void
}

const AppStateContext = createContext<AppStateValue | null>(null)

export function AppStateProvider({
  children
}: {
  children: ReactNode
}): ReactNode {
  const [user, setUser] = useState<UserProfile | null>(null)
  // Токен в хранилище есть — но действителен ли он, знает только
  // сервер: за тридцать дней жизни токена учётную запись могли забанить
  // или удалить. Пока не спросили, приложение показывает заставку.
  const [isBootstrapping, setBootstrapping] = useState(
    () => readToken() !== null
  )

  useEffect(() => {
    if (readToken() === null) return

    let cancelled = false
    api
      .me()
      .then((profile) => {
        if (!cancelled) setUser(profile)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        // Токен протух или отозван — чистим, чтобы не показывать
        // разделы, которые всё равно ответят 401. Сетевой сбой не
        // повод выкидывать сессию: сервер мог просто не подняться.
        if (isRejected(error)) writeToken(null)
      })
      .finally(() => {
        if (!cancelled) setBootstrapping(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const value = useMemo<AppStateValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isBootstrapping,
      signIn: (next, token) => {
        writeToken(token)
        setUser(next)
      },
      signOut: () => {
        writeToken(null)
        setUser(null)
      },
      setUser
    }),
    [user, isBootstrapping]
  )

  return (
    <AppStateContext.Provider value={value}>
      {children}
    </AppStateContext.Provider>
  )
}

/// Сервер отказал в сессии — в отличие от «не дозвонились».
function isRejected(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'kind' in error &&
    (error as { kind: unknown }).kind === 'notAuthenticated'
  )
}

export function useApp(): AppStateValue {
  const value = useContext(AppStateContext)
  if (value === null) {
    throw new Error('useApp вне AppStateProvider')
  }
  return value
}
